import { Box } from '@mui/material';
import { App, Form, Spin } from 'antd';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import { isEqual } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import { constantFormValues } from './root';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import debounce from 'lodash/debounce';
import { hasGlKey, isGrossRentLessThanAdvanceOnEachItem, isSequential, stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import GeneralLeaseInformation from './CustomComponents/cardSections/GeneralLeaseInformation';
import FinancialTermsConditions from './CustomComponents/cardSections/FinancialTermsConditions';
import IFRS16FieldsModifications from './CustomComponents/cardSections/IFRS16FieldsModifications';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import LeasePageHeader from 'components/header/LeasePageHeader';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';
import { useSelector } from 'react-redux';
import { useGetQueryV3Mutation } from '../../../../store/features/api/graphSlice';
import _ from 'lodash';
import { dispatch } from '../../../../store/store';
import { setMaxRecordedDate } from '../../../../store/reducer/lease-management/leaseSlice';
import { useControlData } from './Create';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';

// Cache to store lease data for quick access
const leaseDataCache = {};

dayjs.extend(utc);

const Edit = ({ screen = 'EDIT' }) => {
	const { message: messageApi } = App.useApp();
	// Get cache key before component initializes
	const { state } = useLocation();
	const cacheKey = `${state?._id}-${state?.processCode}`;
	const initialCachedData = leaseDataCache[cacheKey];
	const [tabInstanceId] = useState(() => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const [selectedTab, setSelectedTab] = useState(0);
	// const [searchParams] = useSearchParams();
	const leaseDetailsShowAllRef = useRef();
	// const handleDataLoadingExecutedRef = useRef(false);

	const { lynFormProperties, isLoading } = useControlData();

	const [previousFormData, setPreviousFormData] = useState({});
	const [getLeaseTableData] = useGetQueryV3Mutation();
	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	// Initialize with cached data if available
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);

	const [
		getSingleLeaseData,
		{
			data: singleLeaseData, //viewData
			isLoading: isViewLoading,
		},
	] = useGetSingleIdentityMutation();

	// Use cached data or API data
	const displayLeaseData = cachedLeaseData || singleLeaseData;
	const { profileData } = useSelector((state) => state?.profile);
	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'edit'
			? !(
					displayLeaseData?.activationStatus === 'W-I-P' &&
					displayLeaseData?.recordStatus === 'parked' &&
					profileData?.userId === displayLeaseData?.approverUserId[0]
				)
				? displayLeaseData?.data
				: displayLeaseData?.parkedData
			: [];
	}, [displayLeaseData]);

	//constants
	const hasGlAssigned = hasGlKey(statusData);
	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);
	dayjs.extend(utc);
	const [form] = Form.useForm();
	const navigate = useNavigate();

	// Combined States
	const [formState, setFormState] = useState({
		showFormSubmitModal: false,
		submitPayload: null,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	// Initialize dataFullyLoaded as true if we have cached data
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);

	// State to track original values for comparison
	const [originalValues, setOriginalValues] = useState({
		leaseTerm: null,
		initialDiscountRate: null,
		paymentFrequency: null,
		paymentModality: null,
		grossRents: null,
		advanceAmounts: null,
	});

	// Track if a change row has been added during this edit session
	const [changeRowAdded, setChangeRowAdded] = useState(false);

	// Keep track of the latest form values to prevent duplicate processing
	const lastProcessedValues = useRef({});

	// Track the original length of changes array for reverting
	const [originalChangesLength, setOriginalChangesLength] = useState(0);

	//simplified state setter
	const updateFormState = stateUpdater(setFormState);
	//Single States
	const [rowIndex, setRowIndex] = useState(0);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [updateApi, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] =
		useGetSingleIdentityMutation();

	// Fetch the data when component mounts
	useEffect(() => {
		// Reset tracking states on component mount
		setChangeRowAdded(false);
		lastProcessedValues.current = {};

		// If we don't have cached data or need to reset, show loading
		if (!initialCachedData) {
			setDataFullyLoaded(false);
		}

		// Function to fetch data
		const fetchLeaseData = async () => {
			try {
				const response = await getSingleLeaseData({
					suffixCode: '',
					_id: state?._id ?? '',
					operation: 'get-single',
					processCode: state?.processCode,
				}).unwrap();

				// Store in cache for future use
				if (!initialCachedData || !isEqual(initialCachedData, response)) {
					leaseDataCache[cacheKey] = response;
					setCachedLeaseData(response);
				}
			} catch (error) {
				console.error('Error fetching lease data:', error);
			}
		};

		if (initialCachedData) {
			// If we have cached data, fetch in background without showing loading
			requestAnimationFrame(() => {
				fetchLeaseData();
			});
		} else {
			// No cached data, fetch and show loading
			fetchLeaseData();
		}
	}, [initialCachedData, cacheKey, state?._id, state?.processCode]);

	// Handle API responses and navigation
	useEffect(() => {
		if (isUpdateSuccess) {
			toast.success('Success! Record updated successfully', toastConfig);
			navigate(-2);
		}
		if (isUpdateError) {
			if (updateError?.data?.code === '501') {
				toast.error(updateError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isUpdateSuccess, updateError, isUpdateError]);

	// Set form values and initialize tracking when data loads
	useEffect(() => {
		// Skip if no data yet
		if (!statusData || Object.keys(statusData).length === 0) return;
		// let modifiedLeaseTerm;
		// if (statusData?.paymentFrequency === 'Monthly') {
		// 	modifiedLeaseTerm = statusData?.leaseTerm;
		// } else if (statusData?.paymentFrequency === 'Quarterly') {
		// 	modifiedLeaseTerm = statusData?.leaseTerm / 3;
		// }

		// Map form values from API data
		const viewValues = {
			...statusData,
			// General Lease Information section
			tabIndex: '1',
			modificationSequence: statusData?.modificationSequence,
			leaseId: statusData?.leaseId,
			leaseType: statusData?.leaseType,
			leaseTitle: statusData?.leaseTitle,
			lessorName: statusData?.lessorName,
			paymentFrequency: statusData?.paymentFrequency,
			paymentModality: statusData?.paymentModality,
			creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
			lastModifiedDate: dayjs(statusData?.lastModifiedDate),

			// Financial Terms & Conditions section
			advanceAmount: statusData?.advanceAmount,
			advanceDuration: statusData?.advanceDuration,
			grossRent: statusData?.grossRent,
			leaseTerm: statusData?.leaseTerm,
			leaseStartDate: dayjs(statusData?.leaseStartDate),
			leaseEndDate: dayjs(statusData?.leaseEndDate),
			escalationPercentage: 0,
			initialDiscountRate: statusData?.initialDiscountRate,
			upfrontLeasePayment: statusData?.upfrontLeasePayment,
			initialDirectCost: statusData?.initialDirectCost,
			securityDeposit: statusData?.securityDeposit,
			cashIncentiveAmount: statusData?.cashIncentiveAmount,
			endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),

			//others
			version: String(displayLeaseData?.version),
			identityId: displayLeaseData?._id,
			leaseLiability: statusData?.leaseLiability,
			effectiveDateHistory: statusData?.effectiveDateHistory,
			leaseName: statusData?.leaseName,

			month: statusData?.month,

			agreementFiles: statusData?.agreementFiles,

			discountRateChanges: statusData?.discountRateChanges,
			advanceAmounts: statusData?.advanceAmounts,
			grossRents: statusData?.grossRents,
			grossRentList: statusData?.grossRentList,
			grossRentListSet: statusData?.grossRentListSet,
			advanceAmountListSet: statusData?.advanceAmountListSet,
			advanceAmountList: statusData?.advanceAmountList,
			//changes
			changes: statusData?.changes?.map((item) => {
				const { inputChangeEffectiveDate, ...change } = item;
				return {
					...change,
					inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
				};
			}),
		};

		// Store the original length of changes array
		setOriginalChangesLength(statusData?.changes?.length || 0);

		// Set row index based on existing changes
		setRowIndex(statusData?.changes?.length);

		// Set form values synchronously for immediate display
		form.setFieldsValue(viewValues);

		// For cached data, initialize immediately to avoid delay
		if (initialCachedData) {
			// Use requestAnimationFrame to ensure it happens after render
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		} else {
			// Wait for form to update before capturing original values
			// Use requestAnimationFrame instead of setTimeout for better timing
			requestAnimationFrame(() => {
				initializeFormTracking();
			});
		}
	}, [statusData, initialCachedData]);

	// Extracted function to initialize form tracking
	const initializeFormTracking = () => {
		// Store key field values for change detection
		const originalFieldValues = {
			leaseTerm: form.getFieldValue('leaseTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentModality: form.getFieldValue('paymentModality'),
			grossRents: form.getFieldValue('grossRents'), // Don't stringify, keep as original object
			advanceAmounts: form.getFieldValue('advanceAmounts'), // Don't stringify, keep as original object
		};

		// Also store these in the lastProcessedValues to avoid duplicate processing
		lastProcessedValues.current = { ...originalFieldValues };

		// Store the captured values
		setOriginalValues(originalFieldValues);

		// Mark data as fully loaded AFTER setting original values

		setDataFullyLoaded(true);
	};

	// const [calculateData, setCalculateData] = useState({});
	const [selectHistory, setSelectHistory] = useState(null);

	const manageChangeRows = useCallback(() => {
		// Don't detect changes until data is fully loaded
		if (!dataFullyLoaded) {
			return;
		}

		const currentChangeList = form.getFieldValue('changes') || [];
		const maxAllowedRows = originalChangesLength + 1;

		// If we already have more than max allowed rows, trim to the max
		if (currentChangeList.length > maxAllowedRows) {
			const trimmedList = currentChangeList.slice(0, maxAllowedRows);
			form.setFieldsValue({ changes: trimmedList });
			setRowIndex(maxAllowedRows);
			// Update tracking state
			setChangeRowAdded(maxAllowedRows > originalChangesLength);
			return;
		}

		// Get current values from form
		const currentValues = {
			leaseTerm: form.getFieldValue('leaseTerm'),
			initialDiscountRate: form.getFieldValue('initialDiscountRate'),
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentModality: form.getFieldValue('paymentModality'),
			grossRents: form.getFieldValue('grossRents'),
			advanceAmounts: form.getFieldValue('advanceAmounts'),
		};

		// Check if we've already processed these exact values to prevent duplicate processing
		if (isEqual(currentValues, lastProcessedValues.current)) {
			return;
		}

		// Update last processed values
		lastProcessedValues.current = { ...currentValues };

		// Deep compare values to detect changes
		const hasTermChanged = currentValues.leaseTerm !== originalValues.leaseTerm;
		const hasDiscountRateChanged = currentValues.initialDiscountRate !== originalValues.initialDiscountRate;
		const hasPaymentFrequencyChanged = currentValues.paymentFrequency !== originalValues.paymentFrequency;
		const hasPaymentModalityChanged = currentValues.paymentModality !== originalValues.paymentModality;

		// Use lodash isEqual for deep comparison of arrays
		const normalizeArray = (arr) => {
			if (!Array.isArray(arr)) return arr;
			return [...arr].map((v) => (typeof v === 'string' ? Number(v) || 0 : v || 0)).sort((a, b) => a - b);
		};

		const hasGrossRentsChanged = !isEqual(normalizeArray(currentValues.grossRents), normalizeArray(originalValues.grossRents));
		const hasAdvanceAmountsChanged = !isEqual(normalizeArray(currentValues.advanceAmounts), normalizeArray(originalValues.advanceAmounts));

		// Track if any fields changed
		const hasChanges =
			hasTermChanged ||
			hasDiscountRateChanged ||
			hasPaymentFrequencyChanged ||
			hasPaymentModalityChanged ||
			hasGrossRentsChanged ||
			hasAdvanceAmountsChanged;

		// Check if we should remove the added row when all changes are reverted
		if (!hasChanges && changeRowAdded && currentChangeList.length > originalChangesLength) {
			const updatedList = currentChangeList.slice(0, originalChangesLength);
			form.setFieldsValue({ changes: updatedList });
			setRowIndex(originalChangesLength);
			setChangeRowAdded(false);
			messageApi.info('Changes reverted. The added change record has been removed.');
			return;
		}

		// If no changes detected, exit early
		if (!hasChanges) {
			return;
		}

		// Check if we need to handle an existing row vs create new one
		const hasAutoadedRow = currentChangeList.length > originalChangesLength;
		let shouldCreateNewRow = !hasAutoadedRow;

		if (hasAutoadedRow) {
			const lastRow = currentChangeList[currentChangeList.length - 1];
			// If the last row has an effective date set, don't create a new row
			if (lastRow && lastRow.inputChangeEffectiveDate) {
				shouldCreateNewRow = false;
			}
		}

		// Determine the appropriate change description
		let changeDescription = 'Initial Rent';
		if (hasGrossRentsChanged && hasAdvanceAmountsChanged) {
			changeDescription = 'Change In rent and advance amount';
		} else if (hasGrossRentsChanged) {
			changeDescription = 'Change In rent';
		} else if (hasAdvanceAmountsChanged) {
			changeDescription = 'Change In advance';
		}

		// Create the row data
		const newRow = {
			paymentFrequency: currentValues.paymentFrequency,
			paymentTime: currentValues.paymentModality,
			inputChangeLeaseTerm: currentValues.leaseTerm,
			inputChangeGrossAmount: 0,
			inputChangeDiscountRate: currentValues.initialDiscountRate,
			inputChangeEscalation: form.getFieldValue('escalationPercentage') || 0,
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAdvanceAmounts: currentValues.advanceAmounts,
			inputChangeGrossRents: currentValues.grossRents,
			inputChangeDescription: changeDescription,
		};

		if (shouldCreateNewRow) {
			// Only create a new row if we haven't already added one
			if (currentChangeList.length < maxAllowedRows) {
				newRow.inputChangeEffectiveDate = null;
				const updatedList = [...currentChangeList, newRow];
				form.setFieldsValue({ changes: updatedList });
				setRowIndex(updatedList.length);
				setChangeRowAdded(true);

				// Mark the effective date field as required
				requestAnimationFrame(() => {
					const newRowIndex = updatedList.length - 1;
					form.setFields([
						{
							name: ['changes', newRowIndex, 'inputChangeEffectiveDate'],
							errors: ['Effective Date is required'],
							touched: true,
						},
					]);
				});

				messageApi.info('A new change record has been added. Please set the effective date.');
			}
		} else {
			// Update the existing added row
			const lastRowIndex = currentChangeList.length - 1;
			const lastRow = currentChangeList[lastRowIndex];

			// Preserve the effective date and other fields
			if (lastRow) {
				newRow.inputChangeEffectiveDate = lastRow.inputChangeEffectiveDate;
				if (lastRow.inputChangeMonth) {
					newRow.inputChangeMonth = lastRow.inputChangeMonth;
				}
			}

			const updatedList = [...currentChangeList];
			updatedList[lastRowIndex] = newRow;
			form.setFieldsValue({ changes: updatedList });

			// Notify if description changed
			if (lastRow && lastRow.inputChangeDescription !== changeDescription) {
				messageApi.info(`Change type updated to "${changeDescription}"`);
			}
		}
	}, [form, originalValues, changeRowAdded, dataFullyLoaded, originalChangesLength, lastProcessedValues]);
	// Use a debounced version of the change handler to avoid rapid consecutive calls
	const debouncedManageChangeRows = useMemo(() => debounce(manageChangeRows, 300), [manageChangeRows]);

	// Expose debouncedManageChangeRows to the window object so child components can access it
	useEffect(() => {
		if (screen === 'EDIT') {
			window.debouncedManageChangeRows = debouncedManageChangeRows;
		}

		return () => {
			// Clean up when component unmounts
			if (window.debouncedManageChangeRows === debouncedManageChangeRows) {
				delete window.debouncedManageChangeRows;
			}
		};
	}, [debouncedManageChangeRows, screen]);
	// function compareAndRemoveDuplicates(inputArray) {
	// 	// Check if the array has at least 2 elements
	// 	if (!Array.isArray(inputArray) || inputArray.length < 2) {
	// 		return {
	// 			data: inputArray,
	// 			isChanged: false,
	// 		};
	// 	}
	//
	// 	// Get the last two elements
	// 	const lastElement = inputArray[inputArray.length - 1];
	// 	const secondLastElement = inputArray[inputArray.length - 2];
	//
	// 	// Function to normalize objects for comparison
	// 	const normalizeForComparison = (arr) => {
	// 		if (!Array.isArray(arr)) return arr;
	//
	// 		return arr.map((item) => {
	// 			// Create a deep copy of the item
	// 			const normalizedItem = JSON.parse(JSON.stringify(item));
	//
	// 			// Normalize each inner object
	// 			return normalizedItem.map((obj) => {
	// 				const result = { ...obj };
	//
	// 				// Normalize amount field if it exists - remove commas and standardize format
	// 				if (obj.amount !== undefined && typeof obj.amount === 'string') {
	// 					// Remove commas and convert to a number, then format back to string with 2 decimal places
	// 					const numericValue = parseFloat(obj.amount.replace(/,/g, ''));
	// 					if (!isNaN(numericValue)) {
	// 						result.amount = numericValue.toFixed(2);
	// 					}
	// 				}
	//
	// 				return result;
	// 			});
	// 		});
	// 	};
	//
	// 	// Normalize both arrays for comparison
	// 	const normalizedLast = normalizeForComparison([lastElement])[0];
	// 	const normalizedSecondLast = normalizeForComparison([secondLastElement])[0];
	//
	// 	// Deep compare the normalized elements
	// 	const areEqual = JSON.stringify(normalizedLast) === JSON.stringify(normalizedSecondLast);
	//
	// 	if (areEqual) {
	// 		// If they are the same, remove the last element
	// 		const modifiedArray = inputArray.slice(0, -1);
	// 		return {
	// 			data: modifiedArray,
	// 			isChanged: false,
	// 		};
	// 	} else {
	// 		// If they are different, return the original array
	// 		return {
	// 			data: inputArray,
	// 			isChanged: true,
	// 		};
	// 	}
	// }

	const handleSubmit = async () => {
		const formData = form.getFieldsValue();
		if (formData?.paymentFrequency === 'Quarterly' && formData?.leaseTerm % 3 !== 0) {
			messageApi.error(
				"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
			);
			return;
		}
		if (!isSequential(formData?.grossRentList)) {
			messageApi.error('Gross Rent List must be sequential');
			return;
		}
		if (!isSequential(formData?.advanceAmountList)) {
			messageApi.error('Advance Amount List must be sequential');
			return;
		}
		let grossRentListSet = formData?.grossRentListSet || [];
		let advanceAmountListSet = formData?.advanceAmountListSet || [];
		let effectiveDateHistory = formData?.effectiveDateHistory || [];
		grossRentListSet?.push(formData?.grossRentList);
		// advanceAmountListSet?.push(formData?.advanceAmountList);
		// const { data: grossRentListSetData} = compareAndRemoveDuplicates(grossRentListSet);

		// if (grossRentListSetChanged) {
		// }
		effectiveDateHistory?.push(utcConverter(formData?.changes?.[formData?.changes?.length - 1]?.inputChangeEffectiveDate));

		// Prepare all changes for submission
		const changesPayload = formData.changes?.map((change) => {
			// Ensure the effective date is in the correct format for API
			if (change.inputChangeEffectiveDate) {
				return {
					...change,
					inputChangeAdvance: change.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0),
					inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate),
					upfrontLeasePayment: formData?.upfrontLeasePayment,
					initialDirectCost: formData?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				};
			}
			return change;
		});
		const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents, formData?.advanceAmounts);
		if (!isGrossRentAndAdvanceAmountValid) {
			messageApi.error('Inconsistency between Lease Payment and Advance Adjustment');
			return;
		}
		const editPagePayload = {
			modificationSequence: statusData?.modificationSequence + 1,
			leaseStartDate: formData?.leaseStartDate,
			leaseTitle: formData?.leaseTitle,
			lessorName: formData?.lessorName,
			leaseTerm: formData?.leaseTerm,
			leaseEndDate: formData?.leaseEndDate,
			paymentModality: formData?.paymentModality,
			leaseType: formData?.leaseType,
			initialDiscountRate: formData?.initialDiscountRate,
			grossRentList: formData?.grossRentList,
			paymentFrequency: formData?.paymentFrequency,
			advanceAmountList: formData?.advanceAmountList,
			advanceAmount: formData?.advanceAmount,
			advanceAmounts: formData?.advanceAmounts,
			upfrontLeasePayment: formData?.upfrontLeasePayment,
			initialDirectCost: formData?.initialDirectCost,
			securityDeposit: formData?.securityDeposit,
			grossRents: formData?.grossRents,
			changes: changesPayload,
			modifiedDateTime: utcConverter(dayjs()),
			cashIncentiveAmount: formData?.cashIncentiveAmount,
			paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,
			lastModifiedDate: utcConverter(dayjs()),
			grossRentListSet: grossRentListSet,
			advanceAmountListSet,
			effectiveDateHistory,
			advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
			modifyCount: statusData?.modifyCount + 1,
			editCount: statusData?.editCount,
		};

		updateFormState('showFormSubmitModal', true);
		updateFormState('modalData.head', 'Confirmation');
		updateFormState('modalData.label', 'Do you want to update this lease record?');
		updateFormState('submitPayload', editPagePayload);
	};

	const handleModalSubmit = () => {
		updateApi({
			operation: 'update-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
			_id: state._id,
			version: +singleLeaseData?.version,
		});
	};
	const hasFormDataChanged = () => {
		const currentFormData = form.getFieldsValue();

		// For VIEW mode, we need more specific field checking
		if (screen?.toLowerCase() !== 'create') {
			const currentPayload = {
				advanceAmount: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
				leaseTerm: currentFormData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: currentFormData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(currentFormData?.leaseStartDate?.date()),
				startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
				startYear: String(currentFormData?.leaseStartDate?.year()),
				paymentFrequency: currentFormData?.changes?.[0]?.paymentFrequency,
				paymentTime: currentFormData?.changes?.[0]?.paymentTime,
				advanceDuration: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: currentFormData?.changes?.[0]?.upfrontLeasePayment,
				initialDirectCost: currentFormData?.changes?.[0]?.initialDirectCost,
				securityDeposit: currentFormData?.securityDeposit,
				endOfFinancialYear: currentFormData?.endOfFinancialYear,
				advanceAmounts: currentFormData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: currentFormData?.changes?.[0]?.inputChangeGrossRents,
			};

			const prevPayload = {
				...previousFormData,
			};

			// Compare payloads as JSON strings
			const currentStr = JSON.stringify(currentPayload);
			const prevStr = JSON.stringify(prevPayload);

			if (currentStr !== prevStr) {
				setPreviousFormData(currentPayload);
				return true;
			}
			return false;
		}

		// For CREATE mode
		const currentPayload = {
			advanceAmount: currentFormData?.advanceAmount,
			leaseTerm: currentFormData?.leaseTerm,
			discountRate: currentFormData?.initialDiscountRate,
			startDay: String(currentFormData?.leaseStartDate?.date()),
			startMonth: String(currentFormData?.leaseStartDate?.month() + 1),
			startYear: String(currentFormData?.leaseStartDate?.year()),
			paymentFrequency: currentFormData?.paymentFrequency,
			paymentTime: currentFormData?.paymentModality,
			advanceDuration: currentFormData?.advanceAmounts?.filter((item) => item !== 0)?.length,
			upfrontLeasePayment: currentFormData?.upfrontLeasePayment,
			initialDirectCost: currentFormData?.initialDirectCost,
			securityDeposit: currentFormData?.securityDeposit,
			endOfFinancialYear: currentFormData?.endOfFinancialYear,
			advanceAmounts: currentFormData?.advanceAmounts,
			grossRents: currentFormData?.grossRents,
		};

		const currentStr = JSON.stringify(currentPayload);
		const prevStr = JSON.stringify(previousFormData);

		if (currentStr !== prevStr) {
			setPreviousFormData(currentPayload);
			return true;
		}
		return false;
	};
	// MUI Tab handler
	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);
		// Get the tab label and format it for URL
		// const tabLabel = tabItem[newValue]?.label.toLowerCase().replace(/\s+/g, '-');
		debouncedManageChangeRows();
		if (newValue === 1 && dataFullyLoaded) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded) {
			if (leaseDetailsShowAllRef.current?.loadData) {
				// Check if form data has changed
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};
	useEffect(() => {
		if (dataFullyLoaded) {
			if (actionButtonsRef.current?.flag?.check !== 0) {
				// Check if form data has changed
				if (hasFormDataChanged()) {
					actionButtonsRef.current.handleDataLoading();
				}
			} else {
				actionButtonsRef.current.queryV3Loader();
			}

			if (singleLeaseData?.data?.version) {
				const version = String(singleLeaseData?.data?.version);
				getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, version],
					name: 'leaseSchedulesOnVersion-v6',
				}).then((res) => {
					// 1. Get the results array safely, defaulting to an empty array.
					const results = res?.data?.results || [];

					// 2. Define the statuses you want to check.
					const relevantStatuses = ['Pending', 'Recorded', 'Reversed'];

					// 3. Filter the results to only include items with those statuses.
					const relevantItems = _.filter(results, (item) => relevantStatuses.includes(item.processStatus));

					// 4. Find the single latest item from this filtered list.
					const latestItem = _.maxBy(relevantItems, 'date');

					// 5. Extract the date from that item (it will be `undefined` if no items were found).
					const overallMaxDate = latestItem?.date;

					// 6. Dispatch the result to your Redux store.
					dispatch(
						setMaxRecordedDate({
							minimumDateLimit: overallMaxDate, // Use the new, correct maximum date
							leaseId: String(singleLeaseData?.data?.leaseId),
						})
					);
				});
			}
		}
		if (selectedTab === 0) {
			if (leaseScheduleRef.current?.queryV3Loader) {
				const result = leaseScheduleRef.current.queryV3Loader();
				// console.log(result,'result112233');
			}
		}
	}, [dataFullyLoaded, screen, singleLeaseData]);

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={{
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<GeneralLeaseInformation screen={screen} vendorList={lynFormProperties?.vendorList} />
					</CardAccordion>

					<FinancialTermsConditions screen={screen} />
					<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} initialChangeLength={originalChangesLength} />
					<ActionButtons
						screen={screen}
						statusData={statusData}
						// setCalculateData={setCalculateData}
						handleUpdate={handleSubmit}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
						hasGlAssigned={hasGlAssigned}
						hasCCAssigned={hasCCAssigned}
					/>
				</>
			) || <></>,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<LeaseSchedule
						screen={screen}
						viewData={singleLeaseData}
						dataFullyLoaded={dataFullyLoaded}
						ref={leaseScheduleRef}
						hasGlAssigned={hasGlAssigned}
						hasCCAssigned={hasCCAssigned}
						key={`lease-schedule-${singleLeaseData?.data?.leaseId || 'new'}-${tabInstanceId}`}
					/>
				</>
			),
		},
		{
			key: '3',
			label: 'Modification History',
			children: (
				<>
					<LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
				</>
			),
		},
		{
			key: '4',
			label: 'Accounts Assignment',
			children: (
				<>
					<AccountingInformation screen={screen} identityId={singleLeaseData?._id} />
				</>
			),
		},
		{
			key: '5',
			label: 'General Ledgers',
			children: (
				<>
					<CardAccordion
						title="General Ledger View"
						darkTitle={true}
						sx={{
							mt: 2,
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							fontSize: '16px !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />
					</CardAccordion>
				</>
			),
		},
		...(statusData?.agreementFiles?.length > 0
			? [
					{
						key: '6',
						label: 'Agreements',
						children: <AgreementFiles screen={'VIEW'} />,
					},
				]
			: []),
		// {
		// 	key: '7',
		// 	label: 'Bulk Upload',
		// 	children: (
		// 		<>
		// 			<LeaseBulkUpload />
		// 		</>
		// 	),
		// },
	];

	// Only show spinner if no cached data is available or updating
	const showSpinner = isUpdateLoading || (isViewLoading && !initialCachedData);

	useEffect(() => {
		if (displayLeaseData && displayLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = displayLeaseData.activationStatus;
		}
	}, [displayLeaseData]);

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				{/*<DetailPageHeader image={solution} title="Edit Lease Record" />*/}
				<LeasePageHeader
					createBtn={false}
					tabsItem={tabItem}
					selectedTab={selectedTab}
					title="Modify Lease Record"
					selectTabHandler={selectTabHandler}
					image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
					secondaryTitle={screen === 'CREATE' ? '' : `Lease ID: ${statusData?.leaseId || ''}`}
				/>
				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						//  preserve={true}
					>
						<AntdValuePreview form={form} show={false} />
						{/* <Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<TabPanel key={index} value={selectedTab} index={index}>
									<Box sx={{ mt: 2 }}>{tabItem[selectedTab]?.children}</Box>
								</TabPanel>
							))}
						</Box> */}
						<Box sx={{ mt: 2 }}>
							{tabItem?.map((item, index) => (
								<Box
									key={index}
									style={{
										display: selectedTab === index ? 'block' : 'none',
									}}
								>
									{item.children}
								</Box>
							))}
						</Box>
					</Form>
				</MainCard>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						showModal={formState?.showFormSubmitModal}
						modalData={formState?.modalData}
						setShowModal={setShowFormSubmitModal}
						handleSubmit={handleModalSubmit}
					/>
				)}
				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						setSelectHistory={setSelectHistory}
						selectItem={selectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default Edit;